import React, { useContext, useState } from "react";
import { ReplyAllOutlined } from "@mui/icons-material";
import { Badge, Box } from "@mui/material";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { InferredIcon } from "../Legend";
import { MapContext } from "../MapProvider";

import ColocationLayerDialog from "./ColocationLayerDialog";
import ToggleOptionButton from "./ToggleOptionButton";
import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

const ColocationButton: React.FC<IButtonProps> = ({ icon, title }) => {
  const { drawControl } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [colocateMove, setColocateMove] = useState(drawControl.current?.options?.colocateMove || false);
  const [selectedLayerCount, setSelectedLayerCount] = useState(drawControl.current?.options?.colocatedMoveLayerIds?.length || 0);

  const handleToggleClick = () => {
    const newValue = !colocateMove;
    if (drawControl.current?.options) {
      drawControl.current.options.colocateMove = newValue;
      setColocateMove(newValue);
    }
  };

  const handleConfigureClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSave = (selectedLayerIds: string[]) => {
    if (drawControl.current?.options) {
      drawControl.current.options.colocatedMoveLayerIds = selectedLayerIds;
      // Update layer count but don't automatically enable/disable
      setSelectedLayerCount(selectedLayerIds.length);
    }
  };

  // Get available layers from drawControl
  const editableLayers = drawControl.current?.options?.editableLayers || [];
  const currentEditingLayerId = drawControl.current?.options?.editingLayerId || "";
  const currentSelectedLayerIds = drawControl.current?.options?.colocatedMoveLayerIds || [currentEditingLayerId];

  // Map editableLayers to the format needed by the dialog with icons and proper names
  const availableLayers = useAppSelector((state) => {
    if (!config) return [];

    const layers = drawControl.current?.options?.editableLayers || [];

    return layers
      .map((layer) => {
        const layerData = selectLayerByVersionAndLayerKey(state, { versionId, layerId: layer.ID });
        const layerConfigID = layerData?.Configuration.ID;
        const layerConfig = layerConfigID ? (config.Data.entities[layerConfigID] as LayerConfig) : undefined;

        if (!layerConfig) return null;

        return {
          id: layer.ID,
          name: layerConfig.Label,
          icon: <InferredIcon entity={layerConfig} config={config} />,
        };
      })
      .filter((layer): layer is NonNullable<typeof layer> => layer !== null);
  });

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <ToolbarToggleButton
          title={title || `${colocateMove ? "Disable" : "Enable"} colocation`}
          value="colocateMove"
          onChange={handleToggleClick}
          selected={colocateMove}
          hasOptions
          sx={{ height: 24 }}
        >
          <Badge
            badgeContent={selectedLayerCount}
            color="secondary"
            max={9}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 9,
                height: 14,
                minWidth: 14,
                padding: "0 3px",
              },
            }}
          >
            {icon || <ReplyAllOutlined />}
          </Badge>
        </ToolbarToggleButton>

        <ToggleOptionButton onClick={handleConfigureClick} active={colocateMove} title="Configure colocated layers" />
      </Box>

      <ColocationLayerDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        availableLayers={availableLayers}
        currentEditingLayerId={currentEditingLayerId}
        initialSelectedLayerIds={currentSelectedLayerIds}
        onSave={handleSave}
      />
    </>
  );
};

export default ColocationButton;
